import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, type ReactNode } from "react";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { KpiCard } from "./KpiCard";
import { tokens } from "../theme";
import { platformLabel } from "../utils/devicePresentation";
import type { DevicePlatform, EnergyReport } from "@shared/types/foyer";

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                          */
/* -------------------------------------------------------------------------- */

/** kWh with sensible precision: 1 dp under 100, whole numbers above. */
export function formatKWh(n: number): string {
  return n < 100
    ? n.toLocaleString("sv-SE", { maximumFractionDigits: 1 })
    : Math.round(n).toLocaleString("sv-SE");
}

/** CO₂ in kg, or tonnes once it's large enough to read better that way. */
export function formatCo2(kg: number): string {
  if (kg >= 1000)
    return `${(kg / 1000).toLocaleString("sv-SE", {
      maximumFractionDigits: 1,
    })} t`;
  return `${kg.toLocaleString("sv-SE", { maximumFractionDigits: 1 })} kg`;
}

const RANGE_NOUN: Record<EnergyReport["range"], string> = {
  day: "today",
  week: "this week",
  month: "this month",
};

/* -------------------------------------------------------------------------- */
/* Building blocks                                                             */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/* ---- Single-series area chart (kWh over time) ---------------------------- */
function AreaChart({
  labels,
  data,
  color,
  unit,
}: {
  labels: string[];
  data: number[];
  color: string;
  unit: string;
}) {
  const W = 640;
  const H = 240;
  const padX = 8;
  const padTop = 12;
  const padBottom = 28;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const n = labels.length;
  if (n === 0) return <Box sx={{ height: H }} />;
  const max = Math.max(...data, 0.001) * 1.15;
  const x = (i: number) => padX + (innerW * i) / Math.max(1, n - 1);
  const y = (v: number) => padTop + innerH - (v / max) * innerH;
  const line = data
    .map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`)
    .join(" ");
  const area =
    `M ${x(0)} ${y(0)} ` +
    data.map((v, i) => `L ${x(i)} ${y(v)}`).join(" ") +
    ` L ${x(n - 1)} ${y(0)} Z`;
  // Keep the x-axis from overflowing on the 30-day range.
  const tickEvery = Math.ceil(n / 12);

  return (
    <Box>
      <Box sx={{ width: "100%" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${unit} over time`}
        >
          {[0, 0.5, 1].map((g) => (
            <line
              key={g}
              x1={padX}
              x2={W - padX}
              y1={padTop + innerH * g}
              y2={padTop + innerH * g}
              style={{ stroke: tokens.divider }}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <path d={area} style={{ fill: color, fillOpacity: 0.18 }} />
          <path
            d={line}
            fill="none"
            style={{ stroke: color }}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0.5, mt: 0.5 }}
      >
        {labels.map((l, i) => (
          <Typography
            key={`${l}-${i}`}
            variant="caption"
            sx={{
              fontSize: 11,
              visibility: i % tickEvery === 0 ? "visible" : "hidden",
            }}
          >
            {l}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/* ---- Horizontal bar list (kWh) ------------------------------------------- */
function BarList({
  items,
  emptyLabel,
}: {
  items: { name: string; kWh: number; co2Kg: number }[];
  emptyLabel: string;
}) {
  const max = Math.max(...items.map((i) => i.kWh), 0.001);
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {items.slice(0, 8).map((it) => (
        <Box key={it.name}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2" noWrap sx={{ fontWeight: 500, mr: 1 }}>
              {it.name}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
              {formatKWh(it.kWh)} kWh · {formatCo2(it.co2Kg)}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: tokens.rowAlt,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(it.kWh / max) * 100}%`,
                height: "100%",
                borderRadius: 99,
                background: `linear-gradient(90deg, ${tokens.success}, ${tokens.brand})`,
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/* Panel                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Presentational energy report - KPIs, consumption chart, breakdowns and the
 * per-device table. Shared by the premise-scoped {@link EnergyView} and the
 * company chain dialog. `scope` flips the premise-aware bits: a chain shows the
 * per-premise breakdown + a Premises KPI + a Premise column; a single property
 * shows a Devices KPI + a Room-led table.
 */
export default function EnergyReportPanel({
  report,
  scope,
}: {
  report: EnergyReport;
  scope: "premise" | "company";
}) {
  const isChain = scope === "company";

  const noneControlled =
    report.totals.deviceCount > 0 &&
    report.totals.controllableDeviceCount === 0;

  const platformItems = useMemo(
    () =>
      report.byPlatform.map((p) => ({
        name:
          p.name === "unknown"
            ? "Unknown"
            : platformLabel(p.name as DevicePlatform),
        kWh: p.kWh,
        co2Kg: p.co2Kg,
      })),
    [report]
  );

  return (
    <Stack spacing={2.5}>
      {/* KPI row */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Energy used"
            value={`${formatKWh(report.totals.kWh)} kWh`}
            sub={RANGE_NOUN[report.range]}
            icon={<BoltOutlinedIcon />}
            tint={tokens.warningTint}
            accent={tokens.warning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="CO₂ equivalent"
            value={formatCo2(report.totals.co2Kg)}
            sub={`at ${report.gridIntensity} g/kWh grid`}
            icon={<Co2OutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Saved vs always-on (modelled)"
            value={`${formatKWh(report.totals.savedKwh)} kWh`}
            sub={
              noneControlled
                ? "no device here can be switched off"
                : `${report.totals.savedPct}% · ${formatCo2(
                    report.totals.savedCo2Kg
                  )} CO₂ · modelled`
            }
            icon={<SavingsOutlinedIcon />}
            tint={noneControlled ? undefined : tokens.successTint}
            accent={noneControlled ? undefined : tokens.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isChain ? (
            <KpiCard
              label="Premises"
              value={report.totals.premiseCount.toLocaleString("sv-SE")}
              sub={`${report.totals.deviceCount} devices`}
              icon={<ApartmentOutlinedIcon />}
            />
          ) : (
            <KpiCard
              label="Devices"
              value={report.totals.deviceCount.toLocaleString("sv-SE")}
              sub="in this property"
              icon={<DevicesOtherOutlinedIcon />}
            />
          )}
        </Grid>
      </Grid>

      {/* Consumption over time + platform split */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard
            title="Consumption over time"
            subtitle={`Estimated kWh per ${
              report.range === "day" ? "hour" : "day"
            }`}
          >
            <AreaChart
              labels={report.series.labels}
              data={report.series.kWh}
              color={tokens.success}
              unit="kWh"
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="By platform">
            <BarList items={platformItems} emptyLabel="No devices yet." />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Per-premise breakdown - chain only */}
      {isChain && (
        <SectionCard title="By premise">
          <BarList
            items={report.byPremise}
            emptyLabel="No premises in scope."
          />
        </SectionCard>
      )}

      {/* Device table */}
      <SectionCard
        title="Devices"
        subtitle="Per-device estimate, biggest consumer first"
      >
        {report.devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No devices to report on yet.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader data-testid="energy-table">
              <TableHead>
                <TableRow>
                  {isChain && <TableCell>Premise</TableCell>}
                  <TableCell>Room</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Draw</TableCell>
                  <TableCell align="right">kWh</TableCell>
                  <TableCell align="right">CO₂</TableCell>
                  <TableCell>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.devices.map((d) => (
                  <TableRow key={d.screenId} hover>
                    {isChain && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {d.premiseName}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>{d.roomId ?? "-"}</TableCell>
                    <TableCell>{d.model ?? "-"}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={`On ~${d.profile.onWatts} W · standby ~${d.profile.standbyWatts} W (${d.profile.source})`}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {d.profile.onWatts} W
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{formatKWh(d.kWh)}</TableCell>
                    <TableCell align="right">{formatCo2(d.co2Kg)}</TableCell>
                    <TableCell>
                      {!d.powerControlled ? (
                        // No power control on this device: a schedule can't
                        // park it, so it never contributes a saving. Say that
                        // outright rather than show a state we can't act on.
                        <Tooltip title="This device's shell can't switch the display - power schedules are a no-op here, and it's counted at its always-on baseline.">
                          <Chip
                            size="small"
                            variant="outlined"
                            label="No power control"
                          />
                        </Tooltip>
                      ) : d.powerState ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={d.powerState === "on" ? "warning" : "default"}
                          label={d.powerState === "on" ? "On" : "Standby"}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <Typography variant="caption" color="text.secondary">
        Estimates model panel draw from device model/platform - not metered at
        the wall - and assume an always-on display as the pre-scheduling
        baseline. Standby time is only counted where the device confirmed the
        switch, so a display that ignored a schedule is billed as on. CO₂ uses a{" "}
        {report.gridIntensity} g/kWh grid factor.
      </Typography>
    </Stack>
  );
}
